import { TableClient } from "@azure/data-tables";
import type { TableEntity } from "@azure/data-tables";
import type { TokenCredential } from "@azure/core-auth";
import { makeRowKey } from "@m8t-stack/agent-ledger";
import type { LedgerRow } from "@m8t-stack/agent-ledger/read";
import {
  DEFAULT_SAFETY_LAG_SECONDS,
  ENGINE_VERSION,
  canonicalWorker,
  type AdvancePlan,
  type Cursor,
  type DreamCursor,
  type PkWatermark,
} from "./types.js";

/** The single BrainDreamCursor table. PK is fixed; RowKey = canonical worker. */
export const CURSOR_PARTITION_KEY = "dreamcursor";

/**
 * A point-lookup 404 — either the TABLE is absent (first run anywhere:
 * `TableNotFound`) or the ROW is absent (first run for this worker:
 * `ResourceNotFound`). Both mean "no cursor yet"; neither is an error.
 * Mirrors @azure/data-tables RestError shape (statusCode + body errorCode),
 * matching apps/web/lib/gateway/{conversations,init-tables}.ts.
 */
function isAbsentCursorError(e: unknown): boolean {
  const anyE = e as { statusCode?: number; code?: string; details?: { errorCode?: string } };
  return (
    anyE.statusCode === 404 ||
    anyE.code === "ResourceNotFound" ||
    anyE.code === "TableNotFound" ||
    anyE.details?.errorCode === "TableNotFound" ||
    anyE.details?.errorCode === "ResourceNotFound"
  );
}

interface CursorRow {
  partitionKey: string;
  rowKey: string;
  watermarks: string; // JSON Record<physicalPk, PkWatermark>
  fetchFailures?: string; // JSON Record<conversationId, committed-cycle count>
  safetyLagSeconds?: number;
  engineVersion?: string;
  lastDreamAt?: string;
}

/** The from-the-beginning cursor: empty watermark map, default lag, current engine. */
function absentCursor(canonical: string): DreamCursor {
  return {
    worker: canonical,
    watermarks: {},
    safetyLagSeconds: DEFAULT_SAFETY_LAG_SECONDS,
    engineVersion: ENGINE_VERSION,
  };
}

/**
 * Cursor impl over the BrainDreamCursor Table Storage table.
 * The TableClient is INJECTED (unit tests pass a fake; the entry point
 * constructs the real one) — this class reads no env and no wall clock.
 */
export class TableCursor implements Cursor {
  constructor(private readonly client: TableClient) {}

  /**
   * Point-read the canonical worker's cursor row. Catches BOTH the
   * missing-table (TableNotFound) and missing-row (ResourceNotFound) 404s and
   * returns an absent (from-the-beginning) cursor WITHOUT creating the table or
   * writing anything.
   * Any other error (e.g. 403 RBAC) propagates raw.
   */
  async read(worker: string): Promise<DreamCursor> {
    const canonical = canonicalWorker(worker);
    let row: CursorRow;
    try {
      row = await this.client.getEntity<CursorRow>(CURSOR_PARTITION_KEY, canonical);
    } catch (e) {
      if (isAbsentCursorError(e)) return absentCursor(canonical);
      throw e;
    }
    const watermarks = row.watermarks
      ? (JSON.parse(row.watermarks) as Record<string, PkWatermark>)
      : {};
    const fetchFailures = row.fetchFailures
      ? (JSON.parse(row.fetchFailures) as Record<string, number>)
      : undefined;
    return {
      worker: canonical,
      watermarks,
      ...(fetchFailures && Object.keys(fetchFailures).length > 0 ? { fetchFailures } : {}),
      safetyLagSeconds: row.safetyLagSeconds ?? DEFAULT_SAFETY_LAG_SECONDS,
      engineVersion: row.engineVersion ?? ENGINE_VERSION,
      ...(row.lastDreamAt ? { lastDreamAt: row.lastDreamAt } : {}),
    };
  }

  /**
   * Persist the cursor for the canonical worker. This is the ONLY place that
   * creates the BrainDreamCursor table; a
   * TableAlreadyExists race is benign. The non-dry path calls this, already
   * wired and unit-tested. The per-PK watermark map is serialized to JSON
   * (Table cells are scalar — same rationale as agent-ledger's `artifacts`).
   */
  async advance(worker: string, cursor: DreamCursor): Promise<void> {
    const canonical = canonicalWorker(worker);
    await this.ensureTable();
    const entity: TableEntity = {
      partitionKey: CURSOR_PARTITION_KEY,
      rowKey: canonical,
      watermarks: JSON.stringify(cursor.watermarks),
      safetyLagSeconds: cursor.safetyLagSeconds,
      engineVersion: cursor.engineVersion,
    };
    if (cursor.fetchFailures && Object.keys(cursor.fetchFailures).length > 0) {
      entity.fetchFailures = JSON.stringify(cursor.fetchFailures);
    }
    if (cursor.lastDreamAt) entity.lastDreamAt = cursor.lastDreamAt;
    // "Replace" (not the default "Merge"): an omitted optional column (fetchFailures)
    // must be PURGED on write — Merge would keep a cleared retry map stale forever.
    await this.client.upsertEntity(entity, "Replace");
  }

  private async ensureTable(): Promise<void> {
    try {
      await this.client.createTable();
    } catch (e) {
      if (isTableAlreadyExistsError(e)) return;
      throw e;
    }
  }
}

/** A benign create-race: the table was created concurrently (e.g. two workers'
 *  advance() in one nightly run). Mirrors apps/web/lib/gateway/init-tables.ts. */
function isTableAlreadyExistsError(e: unknown): boolean {
  const anyE = e as { statusCode?: number; code?: string; details?: { errorCode?: string } };
  return (
    anyE.statusCode === 409 &&
    (anyE.code === "TableAlreadyExists" || anyE.details?.errorCode === "TableAlreadyExists")
  );
}

/**
 * The minimal row shape the watermark math reads: the emit-time plus the storage
 * RowKey (the dedup key). `LedgerRow` satisfies this, and so does the per-conv
 * harvest provenance (`NormalizedConversation.provenance.rows`) the corpus cap
 * feeds back through `computeWatermark` — so the partial-advance watermark is
 * derived by the SAME function as the full-corpus one (no reverse-engineering).
 */
export type WatermarkRow = Pick<LedgerRow, "eventTimestamp"> & { rowKey?: string; eventId?: string };

/** The storage RowKey of a ledger row — the unique, lexically-ordered key the
 *  cursor dedups on — no eventId
 *  tiebreak. agent-ledger's entity writes `rowKey`; the read maps it through.
 *  `makeRowKey(eventTimestamp, eventId)` is the deterministic fallback for a
 *  row that didn't surface its storage key. Exported so the harvest can stamp
 *  the SAME key onto a conversation's provenance (pipeline.ts) that the cursor
 *  later dedups against. */
export function rowStorageKey(r: WatermarkRow): string {
  return r.rowKey ?? makeRowKey(r.eventTimestamp, r.eventId ?? "");
}

/**
 * The ISO ts exactly 1ms before a fetch-errored group — the latest watermark
 * that is still STRICTLY below it, so the group (and the overlap window before
 * it) is re-scanned next run. Degrades to the raw ts if it can't
 * be parsed. Shared by buildAdvancePlan (pipeline.ts) and the corpus cap so both
 * apply the IDENTICAL fence.
 */
export function justBefore(failedTs: string): string {
  const t = Date.parse(failedTs);
  return Number.isFinite(t) ? new Date(t - 1).toISOString() : failedTs;
}

const lexMax = (a: string, b: string): string => (a >= b ? a : b);
const lexMin = (a: string, b: string): string => (a <= b ? a : b);

/**
 * Pure watermark math — `now` is INJECTED, never read from the wall
 * clock here (the unit-tested core). Given the rows consumed for one physical
 * PK this run:
 *   ts = min( max(consumed eventTimestamp), now − safetyLag )
 * The clamp to `now − safetyLag` is the silent-drop fix: eventTimestamp is the
 * worker's emit-time, not commit-order, so we never advance past a point that a
 * still-in-flight turn could back-date into. consumedRowKeys = the RowKeys at or
 * after the overlap window start `(ts − safetyLag)` — so a late row that commits
 * with an earlier emit-time inside the lag is re-scanned + deduped next run and
 * never dropped. ISO-8601 UTC sorts lexically (same property the ledger filter
 * relies on), so string compare == time compare.
 */
export function computeWatermark(
  rows: WatermarkRow[],
  now: string,
  safetyLagSeconds: number,
): PkWatermark {
  const lagMs = safetyLagSeconds * 1000;
  const nowMinusLag = new Date(Date.parse(now) - lagMs).toISOString();
  const maxEventTs = rows.reduce<string>((acc, r) => lexMax(acc, r.eventTimestamp), "");
  // No rows → idle this PK at the (now − lag) floor with nothing consumed.
  const ts = maxEventTs ? lexMin(maxEventTs, nowMinusLag) : nowMinusLag;
  const windowStart = new Date(Date.parse(ts) - lagMs).toISOString();
  const consumedRowKeys = rows
    .filter((r) => r.eventTimestamp >= windowStart)
    .map(rowStorageKey);
  return { ts, consumedRowKeys };
}

/**
 * Commit a write-gated AdvancePlan. Called by
 * runDream ONLY after a successful brain write, so a failed write never burns the
 * window. Reuses Cursor.advance (the single DDL site — lazy ensureTable on first
 * use). The plan already carries the fence-corrected per-PK watermarks + this
 * run's consumedRowKeys; this just persists them and stamps lastDreamAt.
 */
export async function commitCursorAdvance(plan: AdvancePlan, cursor: Cursor): Promise<void> {
  const prior = await cursor.read(plan.worker);
  const watermarks: Record<string, PkWatermark> = {};
  for (const [pk, watermark] of Object.entries(plan.watermarks)) {
    const priorWatermark = Object.hasOwn(prior.watermarks, pk) ? prior.watermarks[pk] : undefined;
    if (priorWatermark && watermark.ts <= priorWatermark.ts) {
      // A held fence can re-surface already-consumed rows; keep the dedup memory as
      // a bounded union until the fetch-failure budget releases the fence.
      watermarks[pk] = {
        ts: watermark.ts,
        consumedRowKeys: [...new Set([...priorWatermark.consumedRowKeys, ...watermark.consumedRowKeys])],
      };
    } else {
      watermarks[pk] = watermark;
    }
  }
  const next: DreamCursor = {
    worker: plan.worker,
    watermarks,
    ...(plan.fetchFailures && Object.keys(plan.fetchFailures).length > 0 ? { fetchFailures: plan.fetchFailures } : {}),
    safetyLagSeconds: prior.safetyLagSeconds || DEFAULT_SAFETY_LAG_SECONDS,
    engineVersion: ENGINE_VERSION,
    lastDreamAt: new Date().toISOString(),
  };
  await cursor.advance(plan.worker, next);
}

/**
 * Zero-LLM pre-check. Skip (true) ONLY when
 * EVERY physical PK present in `maxEventTsByPk` has its newest eventTimestamp
 * at/below ITS OWN watermark. A PK present in the rows but ABSENT from the cursor
 * watermarks (a brand-new physical PK — e.g. new a2a traffic) is never
 * at/below a watermark, so the worker is NOT skipped (the global-max bug). An
 * empty watermark map (never-seen worker) is never a skip.
 */
export function skipIfNoNew(maxEventTsByPk: Record<string, string>, cursor: DreamCursor): boolean {
  if (Object.keys(cursor.watermarks).length === 0) return false;
  const pks = Object.keys(maxEventTsByPk);
  if (pks.length === 0) return true; // no ledger rows at all → nothing new
  return pks.every((pk) => {
    if (!Object.hasOwn(cursor.watermarks, pk)) return false; // new PK with rows → not a skip
    return (maxEventTsByPk[pk] ?? "") <= cursor.watermarks[pk].ts;
  });
}

/**
 * The newest eventTimestamp per physical PK — the cheap bounded ledger read that
 * feeds skipIfNoNew. Pure; ISO-8601 UTC sorts lexically. Keyed on
 * the raw physical PK (agentName), matching the cursor's per-PK watermark keys.
 */
export function computeMaxEventTsByPk(rows: LedgerRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const pk = r.agentName;
    if (!Object.hasOwn(out, pk) || r.eventTimestamp > out[pk]) out[pk] = r.eventTimestamp;
  }
  return out;
}

/** The BrainDreamCursor Table Storage table name. */
export const CURSOR_TABLE_NAME = "BrainDreamCursor";

/**
 * Edge constructor for the live `Cursor`: builds a `TableClient` for the
 * BrainDreamCursor table from a credential + the storage account's table
 * endpoint, then wraps it in `TableCursor` (which reads no env / wall clock).
 * The dry path only calls `.read()`, which tolerates a missing table
 * (only `.advance()` does DDL). Unit tests inject fakes and never reach
 * this; the CLI's `defaultDeps` calls it on the live path.
 */
export function makeTableCursor(credential: TokenCredential, tableEndpoint: string): Cursor {
  const client = new TableClient(tableEndpoint, CURSOR_TABLE_NAME, credential);
  return new TableCursor(client);
}

/** Human-readable, secret-free dry-run line: the watermark + lag advance()
 *  WOULD write per physical PK. */
export function formatCursorPreview(cursor: DreamCursor): string {
  const head = `cursor: worker=${cursor.worker} safetyLag=${String(cursor.safetyLagSeconds)}s engine=${cursor.engineVersion}`;
  const pks = Object.entries(cursor.watermarks);
  if (pks.length === 0) return `${head}\n  (from the beginning — no prior watermark)`;
  const lines = pks.map(
    ([pk, w]) => `  ${pk} → ts=${w.ts} (${String(w.consumedRowKeys.length)} consumed)`,
  );
  return [head, ...lines].join("\n");
}
